// src/pages/BlitzCreate/Create.js
import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Toolbar,
  useMediaQuery,
  useTheme,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Stack,
  Container
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Flag,
  Collections,
  RateReview,
  CalendarMonth,
  SmartToy,
  Group,
  Extension,
  BarChart,
  Link,
  Contacts,
  SyncAlt
} from "@mui/icons-material";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import createImage from "../../../Components/globalAssets/blackwhite.png";

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const stats = [
  { value: "10K+", label: "Campaigns Launched" },
  { value: "50K+", label: "Creators Onboarded" },
  { value: "98%", label: "Collaboration Success" },
  { value: "24h", label: "Avg Response Time" }
];

const modules = [
  {
    icon: <Flag fontSize="large" />,
    title: "Creative Briefs & Templates",
    description:
      "Dynamic brief builder with pre-configured social, video, and email templates; one-click campaign launch."
  },
  {
    icon: <Collections fontSize="large" />,
    title: "Asset Library & Versioning",
    description:
      "Centralized media repo with tags, side-by-side drafts, timestamped comments, and locked archives."
  },
  {
    icon: <RateReview fontSize="large" />,
    title: "Review & Approval Workflows",
    description:
      "Multi-stage sign-off gates and in-app annotations for stakeholders to comment, draw, and approve."
  },
  {
    icon: <CalendarMonth fontSize="large" />,
    title: "Content Calendar & Publishing",
    description:
      "Drag-drop scheduler plus cross-platform auto-publish to Instagram, TikTok, LinkedIn, and more."
  },
  {
    icon: <SmartToy fontSize="large" />,
    title: "AI-Assisted Creation",
    description:
      "Smart copy & caption generator and one-click reflow for 1:1, 9:16, 16:9, or custom aspect ratios."
  },
  {
    icon: <Group fontSize="large" />,
    title: "Team Collaboration & Roles",
    description:
      "Task/comment threads, real-time status, and granular permissions for drafting, reviewing, or viewing."
  },
  {
    icon: <Extension fontSize="large" />,
    title: "Integrations & Extensions",
    description:
      "Sync with Google Drive, Figma, Adobe CC; push notifications to Slack, Teams, or in-app alerts."
  },
  {
    icon: <BarChart fontSize="large" />,
    title: "Reporting & Insights",
    description:
      "Engagement dashboards, ROI tracking, and content-to-budget tagging for direct revenue impact."
  },
  {
    icon: <Link fontSize="large" />,
    title: "Link-in-Bio Tools",
    description:
      "Build branded microsites, A/B test headlines/buttons, and track click-through analytics."
  },
  {
    icon: <Contacts fontSize="large" />,
    title: "Contact Management & CRM",
    description:
      "Store creator/brand profiles, deal history, notes, pipeline views, and automated follow-up triggers."
  },
  {
    icon: <SyncAlt fontSize="large" />,
    title: "Zap Feed Posting",
    description:
      "Native Zapier integration to auto-queue posts across channels based on events and approvals."
  }
];

export default function Create() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const accentColor = theme.palette.mode === "light" ? "#0d47a1" : "#90caf9";
  const statsColor = theme.palette.mode === "light" ? "#1e88e5" : "#4fc3f7";
  const refs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const handleNavigate = () => navigate(routes.onboard);

  const ModuleCard = ({ icon, title, description }) => (
    <Paper
      component={motion.div}
      variants={fadeIn}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 3,
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: theme.shadows[2],
        transition: "transform 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6]
        }
      }}
    >
      <Box sx={{ color: accentColor, mb: 2 }}>{icon}</Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box
        component="section"
        ref={el => (refs.current['hero'] = el)}
        sx={{
          py: 10,
          background: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "#fff"
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography
                component={motion.div}
                variants={fadeIn}
                variant={isMobile ? "h4" : "h2"}
                gutterBottom
                sx={{ fontWeight: 800 }}
              >
                Power Your Creative Workflow
              </Typography>
              <Typography
                component={motion.div}
                variants={fadeIn}
                variant="h5"
                sx={{ mb: 4, fontWeight: 300 }}
              >
                From briefs to publish—streamline ideation, review, and launch in one platform
              </Typography>
              <Button
                component={motion.div}
                variants={fadeIn}
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleNavigate}
                sx={{
                  fontSize: "1.1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: theme.shadows[4]
                }}
              >
                Get Started
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                variants={fadeIn}
                sx={{
                  background: `linear-gradient(145deg, ${theme.palette.background.paper}, #e0e0e0)`,
                  borderRadius: 4,
                  p: 2,
                  boxShadow: theme.shadows[10]
                }}
              >
                <img
                  src={createImage}
                  alt="Blitz Create Dashboard"
                  style={{
                    width: '100%',
                    borderRadius: 12,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Modules Section */}
      <Box
        id="modules"
        ref={el => (refs.current['modules'] = el)}
        sx={{ py: 8 }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 6 }}
          >
            Key Create Modules
          </Typography>

          <Grid container spacing={4}>
            {modules.map((mod, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <ModuleCard
                  icon={mod.icon}
                  title={mod.title}
                  description={mod.description}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)`
        }}
      >
        <Container maxWidth="sm">
          <Stack alignItems="center" textAlign="center" spacing={3}>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, color: "#fff" }}
            >
              Ready to Elevate Your Content Creation?
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 300 }}
            >
              Join thousands of teams and creators on Blitz Create
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleNavigate}
              sx={{
                fontSize: "1.1rem",
                px: 6,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: theme.shadows[10],
                mt: 2
              }}
            >
              Get Started Free
            </Button>
          </Stack>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}
